// Section 18 justice policy (a response plus "ask each case") and guard mandate selectors, as
// native radio groups and a checkbox (keyboard reachable). Locked responses are disabled with the
// reason in words (execution: common hall and trust 60; imprisonment: a jail). Props only.
import type { GuardMandateOption, JusticeResponse } from '../../../sim/api';
import type { JusticeVM } from '../../../viewModels/justice';
import { justiceText as t } from '../../text/en';

interface Props {
  vm: JusticeVM;
  disabled: boolean;
  onPolicy: (response: JusticeResponse, askEachCase: boolean) => void;
  onMandate: (mandate: GuardMandateOption) => void;
}

export function PolicyControls({ vm, disabled, onPolicy, onMandate }: Props) {
  const p = vm.policy;
  const m = vm.mandate;
  return (
    <section class="justice__section" aria-labelledby="justice-policy-title">
      <h4 id="justice-policy-title">
        {t.policyTitle}
        {p.pending && <span class="justice-tag"> ({t.changesNextSeason})</span>}
      </h4>
      <p class="justice-muted">{t.policyHint}</p>
      <fieldset class="justice-choice" disabled={disabled}>
        <legend>{t.policyTitle}</legend>
        {p.options.map((o) => {
          const id = `justice-policy-${o.response}`;
          return (
            <div key={o.response} class={`justice-option${o.available ? '' : ' is-locked'}`}>
              <input
                type="radio"
                id={id}
                name="justice-policy"
                value={o.response}
                checked={p.response === o.response}
                disabled={!o.available}
                aria-describedby={`${id}-hint`}
                onChange={() => onPolicy(o.response, p.askEachCase)}
              />
              <label for={id}>{t.responses[o.response]}</label>
              <span id={`${id}-hint`} class="justice-muted justice-option__hint">
                {o.block === null ? t.responseHints[o.response] : t.blocks[o.block]}
              </span>
            </div>
          );
        })}
        <div class="justice-option">
          <input
            type="checkbox"
            id="justice-ask-each-case"
            checked={p.askEachCase}
            onChange={(e) => onPolicy(p.response, e.currentTarget.checked)}
          />
          <label for="justice-ask-each-case">{t.askEachCase}</label>
        </div>
      </fieldset>
      <fieldset class="justice-choice" disabled={disabled}>
        <legend>
          {t.mandateTitle}
          {m.pending && <span class="justice-tag"> ({t.changesNextSeason})</span>}
        </legend>
        {m.options.map((option) => {
          const id = `justice-mandate-${option}`;
          return (
            <div key={option} class="justice-option">
              <input
                type="radio"
                id={id}
                name="justice-mandate"
                value={option}
                checked={m.value === option}
                aria-describedby={`${id}-hint`}
                onChange={() => onMandate(option)}
              />
              <label for={id}>{t.mandates[option]}</label>
              <span id={`${id}-hint`} class="justice-muted justice-option__hint">
                {t.mandateHints[option]}
              </span>
            </div>
          );
        })}
      </fieldset>
    </section>
  );
}
